import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    Typography,
    TextField,
    MenuItem,
    Button,
    FormControl,
    InputLabel,
    Select,
    CircularProgress,
    Grid,
    Chip,
    Tooltip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Slider,
    IconButton
} from '@mui/material';
import axios from 'axios';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';

// Получаем API URL из переменной окружения
const API_URL = process.env.REACT_APP_API_URL || '';

// Функции для определения цвета в зависимости от значения
const getRoiColor = (roi) => {
    if (roi > 100) return 'success.main'; // Темно-зеленый для очень хорошего ROI (>100%)
    if (roi > 0) return 'success.light'; // Светло-зеленый для положительного ROI
    if (roi < -20) return 'error.main'; // Темно-красный для очень плохого ROI (<-20%)
    if (roi < 0) return 'error.light'; // Светло-красный для отрицательного ROI
    return 'text.primary'; // Обычный цвет для нуля
};

const getCRColor = (cr) => {
    if (cr > 15) return 'success.main'; // Очень хорошая конверсия
    if (cr > 5) return 'success.light'; // Хорошая конверсия
    if (cr < 1) return 'error.light'; // Плохая конверсия
    return 'text.primary'; // Обычный цвет
};

const getProfitColor = (profit) => {
    if (profit > 5000) return 'success.main'; // Очень хорошая прибыль
    if (profit > 0) return 'success.light'; // Положительная прибыль
    if (profit < -1000) return 'error.main'; // Большой убыток
    if (profit < 0) return 'error.light'; // Убыток
    return 'text.primary'; // Нулевая прибыль
};

// Базовые пороговые значения (дефолтные)
const DEFAULT_PERFORMANCE_THRESHOLDS = {
    minRoi: 20, // Минимально приемлемый ROI в процентах
    minCr: 3,   // Минимально приемлемая конверсия в процентах
    costWarning: 1000, // Порог расходов для предупреждения
    minClicks: 500, // Минимальное количество кликов для достоверной статистики
    minCost: 3000 // Минимальная сумма расходов для достоверной статистики
};

// Функция оценки эффективности рекламного объявления
const evaluateAdPerformance = (adData, globalAverages, customThresholds = DEFAULT_PERFORMANCE_THRESHOLDS) => {
    const warnings = [];
    let score = 100; // Начальная оценка
    
    // Используем предоставленные пороги или дефолтные
    const thresholds = {
        ...DEFAULT_PERFORMANCE_THRESHOLDS,
        ...customThresholds
    };
    
    // Расчет метрик
    const cr = adData.clicks > 0 ? (adData.leads / adData.clicks * 100) : 0;
    const profit = adData.revenue - adData.cost;
    const roi = adData.cost > 0 ? ((profit / adData.cost) * 100) : 0;
    
    // Проверка статистической значимости - это теперь ПЕРВАЯ проверка
    const hasEnoughData = adData.clicks >= thresholds.minClicks || adData.cost >= thresholds.minCost;
    
    if (!hasEnoughData) {
        warnings.push({
            type: 'data',
            severity: 'info',
            message: `Недостаточно данных: ${adData.clicks} кликов, ${adData.cost.toFixed(2)} руб. расходов`
        });
        
        // Возвращаем нейтральный статус для объявлений с недостаточными данными
        return {
            score: 50, // Нейтральная оценка
            status: 'info', // Новый статус - информационный
            warnings,
            hasEnoughData: false
        };
    }
    
    // Только если есть достаточно данных, проводим остальные проверки
    
    // Проверка ROI
    if (roi < thresholds.minRoi) {
        warnings.push({
            type: 'roi',
            severity: roi < 0 ? 'high' : 'medium',
            message: `ROI ниже целевого: ${roi.toFixed(2)}%`
        });
        score -= (thresholds.minRoi - roi) * 2;
    }
    
    // Проверка CR
    if (cr < thresholds.minCr) {
        warnings.push({
            type: 'cr',
            severity: cr < globalAverages.cr / 2 ? 'high' : 'medium',
            message: `Низкая конверсия: ${cr.toFixed(2)}%`
        });
        score -= (thresholds.minCr - cr) * 10;
    }
    
    // Оценка по расходам
    if (adData.cost > thresholds.costWarning && roi < 0) {
        warnings.push({
            type: 'cost',
            severity: 'high',
            message: `Высокий расход (${adData.cost.toFixed(2)} руб.) при отрицательном ROI`
        });
        score -= 15;
    }
    
    return {
        score: Math.max(0, Math.min(100, score)), // Ограничиваем от 0 до 100
        status: score < 40 ? 'critical' : score < 70 ? 'warning' : 'good',
        warnings,
        hasEnoughData: true
    };
};

// Компонент для отображения списка проблемных объявлений
const ProblemAdsList = ({ ads, globalAverages, thresholds = DEFAULT_PERFORMANCE_THRESHOLDS }) => {
    // Оценка эффективности для каждого объявления
    const adsWithPerformance = ads.map(ad => {
        const cr = ad.clicks > 0 ? (ad.leads / ad.clicks * 100) : 0;
        const profit = ad.revenue - ad.cost;
        const roi = ad.cost > 0 ? ((profit / ad.cost) * 100) : 0;
        
        const performance = evaluateAdPerformance(
            { ...ad, cr, roi, profit },
            globalAverages,
            thresholds
        );
        
        return { ...ad, performance, cr, roi, profit };
    });
    
    // Фильтруем только проблемные объявления с достаточным количеством данных
    const problemAds = adsWithPerformance
        .filter(ad => ad.performance.status === 'critical' && ad.performance.hasEnoughData)
        .sort((a, b) => a.performance.score - b.performance.score); // Сначала с самыми низкими оценками
    
    if (problemAds.length === 0) {
        return (
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'success.light', color: 'white' }}>
                <Typography variant="subtitle1">
                    На данный момент нет объявлений, требующих срочного внимания
                </Typography>
            </Paper>
        );
    }
    
    return (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light', color: 'white' }}>
            <Typography variant="subtitle1" gutterBottom>
                Обнаружены объявления, требующие срочного внимания ({problemAds.length})
            </Typography>
            <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
                {problemAds.map(ad => (
                    <Box key={ad.adId} sx={{ p: 1, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                        <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={2}>
                                <strong>ID: {ad.adId}</strong>
                            </Grid>
                            <Grid item xs={4}>
                                {ad.performance.warnings.map((warning, idx) => (
                                    <Typography key={idx} variant="body2">
                                        {warning.message}
                                    </Typography>
                                ))}
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="body2">
                                    Клики: {ad.clicks}
                                </Typography>
                                <Typography variant="body2">
                                    Лиды: {ad.leads}
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="body2">
                                    CR: {ad.cr.toFixed(2)}%
                                </Typography>
                                <Typography variant="body2">
                                    ROI: {ad.roi.toFixed(2)}%
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="body2">
                                    Расход: {ad.cost.toFixed(2)} руб.
                                </Typography>
                                <Typography variant="body2">
                                    Прибыль: {ad.profit.toFixed(2)} руб.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
};

// Диалог настройки пороговых значений
const ThresholdsSettingsDialog = ({ open, onClose, thresholds, onSave }) => {
    const [localThresholds, setLocalThresholds] = useState({ ...thresholds });
    
    useEffect(() => {
        setLocalThresholds({ ...thresholds });
    }, [thresholds, open]);
    
    const handleChange = (name, value) => {
        setLocalThresholds(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    const handleSave = () => {
        onSave(localThresholds);
        onClose();
    };
    
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Настройки параметров эффективности</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <Typography gutterBottom>
                        Минимальный приемлемый ROI (%): {localThresholds.minRoi}
                    </Typography>
                    <Slider
                        value={localThresholds.minRoi}
                        onChange={(_, value) => handleChange('minRoi', value)}
                        min={-50}
                        max={200}
                        step={5}
                        valueLabelDisplay="auto"
                        marks={[
                            { value: 0, label: '0%' },
                            { value: 100, label: '100%' }
                        ]}
                    />
                    
                    <Typography gutterBottom sx={{ mt: 3 }}>
                        Минимальная приемлемая конверсия (%): {localThresholds.minCr}
                    </Typography>
                    <Slider
                        value={localThresholds.minCr}
                        onChange={(_, value) => handleChange('minCr', value)}
                        min={0}
                        max={20}
                        step={0.5}
                        valueLabelDisplay="auto"
                        marks={[
                            { value: 0, label: '0%' },
                            { value: 10, label: '10%' }
                        ]}
                    />
                    
                    <Typography gutterBottom sx={{ mt: 3 }}>
                        Порог расходов для предупреждения (руб.): {localThresholds.costWarning}
                    </Typography>
                    <Slider
                        value={localThresholds.costWarning}
                        onChange={(_, value) => handleChange('costWarning', value)}
                        min={100}
                        max={10000}
                        step={100}
                        valueLabelDisplay="auto"
                        marks={[
                            { value: 1000, label: '1000₽' },
                            { value: 5000, label: '5000₽' }
                        ]}
                    />
                    
                    <Typography gutterBottom sx={{ mt: 3 }}>
                        Минимальное количество кликов для анализа: {localThresholds.minClicks}
                    </Typography>
                    <Slider
                        value={localThresholds.minClicks}
                        onChange={(_, value) => handleChange('minClicks', value)}
                        min={100}
                        max={1000}
                        step={50}
                        valueLabelDisplay="auto"
                        marks={[
                            { value: 100, label: '100' },
                            { value: 500, label: '500' },
                            { value: 1000, label: '1000' }
                        ]}
                    />
                    
                    <Typography gutterBottom sx={{ mt: 3 }}>
                        Минимальная сумма расходов для анализа (руб.): {localThresholds.minCost}
                    </Typography>
                    <Slider
                        value={localThresholds.minCost}
                        onChange={(_, value) => handleChange('minCost', value)}
                        min={500}
                        max={10000}
                        step={500}
                        valueLabelDisplay="auto"
                        marks={[
                            { value: 1000, label: '1000₽' },
                            { value: 3000, label: '3000₽' },
                            { value: 5000, label: '5000₽' }
                        ]}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Отмена</Button>
                <Button onClick={handleSave} variant="contained">Сохранить</Button>
            </DialogActions>
        </Dialog>
    );
};

const AdsStats = () => {
    const [loading, setLoading] = useState(false);
    const [adsData, setAdsData] = useState([]);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [period, setPeriod] = useState('today');
    
    // Состояние для пороговых значений
    const [performanceThresholds, setPerformanceThresholds] = useState(DEFAULT_PERFORMANCE_THRESHOLDS);
    // Состояние для диалога настроек
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    
    // Добавляем состояние для глобальных средних показателей
    const [globalAverages, setGlobalAverages] = useState({
        cr: 0,
        roi: 0
    });
    
    useEffect(() => {
        // Установка дат в зависимости от выбранного периода
        const now = new Date();
        
        switch(period) {
            case 'today':
                setStartDate(new Date(now.setHours(0, 0, 0, 0)));
                setEndDate(new Date());
                break;
            case 'yesterday':
                const yesterday = new Date(now);
                yesterday.setDate(yesterday.getDate() - 1);
                yesterday.setHours(0, 0, 0, 0);
                setStartDate(yesterday);
                const yesterdayEnd = new Date(yesterday);
                yesterdayEnd.setHours(23, 59, 59, 999);
                setEndDate(yesterdayEnd);
                break;
            case 'week':
                const weekStart = new Date(now);
                weekStart.setDate(weekStart.getDate() - 7);
                weekStart.setHours(0, 0, 0, 0);
                setStartDate(weekStart);
                setEndDate(new Date());
                break;
            case 'month':
                const monthStart = new Date(now);
                monthStart.setMonth(monthStart.getMonth() - 1);
                monthStart.setHours(0, 0, 0, 0);
                setStartDate(monthStart);
                setEndDate(new Date());
                break;
            default:
                // Не меняем даты, если выбран custom период
                break;
        }
    }, [period]);

    useEffect(() => {
        if (startDate && endDate) {
            loadStats();
        }
    }, [startDate, endDate]);

    // Форматирование даты в строку для ввода
    const formatDateForInput = (date) => {
        if (!date) return '';
        return date.toISOString().split('T')[0];
    };

    const loadStats = async (forceRefresh = false) => {
        if (!startDate || !endDate) return;
        
        setLoading(true);
        try {
            // Добавляем параметры запроса
            const params = {
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString()
            };
            
            // Добавляем параметр для принудительного обновления кэша
            if (forceRefresh) {
                params._t = new Date().getTime();
            }
            
            console.log('Загрузка статистики по объявлениям:', params);
            
            // Запрос со всеми лидами для подсчета общего количества
            const allLeadsResponse = await axios.get(`${API_URL}/admin/stats/ads`, {
                params,
                withCredentials: true
            });
            
            console.log('Получено объявлений:', allLeadsResponse.data.length);
            
            // Дополняем параметры для запроса подтвержденных лидов
            const confirmedParams = {
                ...params,
                leadsStatus: "confirmed"
            };
            
            // Запрос только с подтвержденными лидами для подсчета дохода
            const confirmedLeadsResponse = await axios.get(`${API_URL}/admin/stats/ads`, {
                params: confirmedParams,
                withCredentials: true
            });
            
            // Объединяем данные: берем количество лидов из первого запроса, а доход из второго
            const combinedData = allLeadsResponse.data.map(adData => {
                // Ищем соответствующую запись с подтвержденными лидами
                const confirmedData = confirmedLeadsResponse.data.find(item => item.adId === adData.adId);
                
                return {
                    ...adData,
                    // Оставляем общее количество лидов
                    revenue: confirmedData ? confirmedData.revenue : 0, // Берем доход только от подтвержденных лидов
                };
            });
            
            // Рассчитываем глобальные средние показатели
            const totalClicks = combinedData.reduce((sum, ad) => sum + ad.clicks, 0);
            const totalLeads = combinedData.reduce((sum, ad) => sum + ad.leads, 0);
            const totalRevenue = combinedData.reduce((sum, ad) => sum + ad.revenue, 0);
            const totalCost = combinedData.reduce((sum, ad) => sum + ad.cost, 0);
            
            setGlobalAverages({
                cr: totalClicks > 0 ? (totalLeads / totalClicks * 100) : 0,
                roi: totalCost > 0 ? (((totalRevenue - totalCost) / totalCost) * 100) : 0
            });
            
            setAdsData(combinedData);
        } catch (error) {
            console.error('Ошибка при загрузке статистики по объявлениям:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        console.log('Нажата кнопка "Обновить"', {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
        });
        loadStats(true); // Вызываем с параметром обновления кэша
    };

    const handlePeriodChange = (event) => {
        setPeriod(event.target.value);
    };

    const handleStartDateChange = (event) => {
        setStartDate(new Date(event.target.value));
    };

    const handleEndDateChange = (event) => {
        setEndDate(new Date(event.target.value));
    };

    // Сохранение пороговых значений
    const handleSaveThresholds = (newThresholds) => {
        setPerformanceThresholds(newThresholds);
        
        // Можно также сохранить в localStorage для сохранения между сессиями
        localStorage.setItem('adStatsThresholds', JSON.stringify(newThresholds));
    };
    
    // Загрузка сохраненных пороговых значений при инициализации
    useEffect(() => {
        const savedThresholds = localStorage.getItem('adStatsThresholds');
        if (savedThresholds) {
            try {
                const parsedThresholds = JSON.parse(savedThresholds);
                setPerformanceThresholds(parsedThresholds);
            } catch (e) {
                console.error('Ошибка при загрузке сохраненных настроек:', e);
            }
        }
    }, []);
    
    // Передаем актуальные пороги в функцию оценки эффективности
    const evaluateAdEfficiency = (adData, globalAvgs) => {
        return evaluateAdPerformance(adData, globalAvgs, performanceThresholds);
    };

    // Отображение статуса объявления в таблице
    const renderPerformanceStatus = (performance) => {
        switch (performance.status) {
            case 'critical':
                return (
                    <Tooltip title={performance.warnings.map(w => w.message).join('\n')}>
                        <ErrorIcon color="error" />
                    </Tooltip>
                );
            case 'warning':
                return (
                    <Tooltip title={performance.warnings.map(w => w.message).join('\n')}>
                        <WarningIcon color="warning" />
                    </Tooltip>
                );
            case 'info':
                return (
                    <Tooltip title="Недостаточно данных для анализа">
                        <Chip 
                            label="Мало данных" 
                            color="info" 
                            size="small" 
                            variant="outlined" 
                        />
                    </Tooltip>
                );
            default:
                return (
                    <Tooltip title="Хорошая эффективность">
                        <CheckCircleIcon color="success" />
                    </Tooltip>
                );
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" component="h2">
                    Статистика по рекламным объявлениям
                </Typography>
                <IconButton 
                    color="primary" 
                    onClick={() => setSettingsDialogOpen(true)}
                    title="Настройки анализа эффективности"
                >
                    <SettingsIcon />
                </IconButton>
            </Box>
            
            {/* Диалог настроек */}
            <ThresholdsSettingsDialog 
                open={settingsDialogOpen}
                onClose={() => setSettingsDialogOpen(false)}
                thresholds={performanceThresholds}
                onSave={handleSaveThresholds}
            />
            
            {/* Отображаем список проблемных объявлений */}
            {!loading && adsData.length > 0 && (
                <ProblemAdsList 
                    ads={adsData} 
                    globalAverages={globalAverages}
                    thresholds={performanceThresholds} 
                />
            )}
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                        <InputLabel id="period-select-label">Период</InputLabel>
                        <Select
                            labelId="period-select-label"
                            id="period-select"
                            value={period}
                            label="Период"
                            onChange={handlePeriodChange}
                        >
                            <MenuItem value="today">Сегодня</MenuItem>
                            <MenuItem value="yesterday">Вчера</MenuItem>
                            <MenuItem value="week">Последние 7 дней</MenuItem>
                            <MenuItem value="month">Последние 30 дней</MenuItem>
                            <MenuItem value="custom">Произвольный период</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                
                {period === 'custom' && (
                    <>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="С даты"
                                type="date"
                                value={formatDateForInput(startDate)}
                                onChange={handleStartDateChange}
                                fullWidth
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="По дату"
                                type="date"
                                value={formatDateForInput(endDate)}
                                onChange={handleEndDateChange}
                                fullWidth
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    min: formatDateForInput(startDate)
                                }}
                            />
                        </Grid>
                    </>
                )}
                
                <Grid item xs={12} md={period === 'custom' ? 3 : 9}>
                    <Button 
                        variant="contained" 
                        onClick={handleRefresh}
                        sx={{ height: '56px' }}
                        fullWidth
                    >
                        Обновить
                    </Button>
                </Grid>
            </Grid>
            
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="статистика по объявлениям">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID объявления</TableCell>
                                <TableCell>Статус</TableCell>
                                <TableCell>Клики</TableCell>
                                <TableCell>Лиды</TableCell>
                                <TableCell>CR (%)</TableCell>
                                <TableCell>Расход (руб.)</TableCell>
                                <TableCell>Доход (руб.)</TableCell>
                                <TableCell>Прибыль (руб.)</TableCell>
                                <TableCell>ROI (%)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {adsData.length > 0 ? (
                                adsData.map((ad) => {
                                    const cr = ad.clicks > 0 ? (ad.leads / ad.clicks * 100) : 0;
                                    const profit = ad.revenue - ad.cost;
                                    const roi = ad.cost > 0 ? ((profit / ad.cost) * 100) : 0;
                                    
                                    // Оцениваем эффективность объявления
                                    const performance = evaluateAdEfficiency(
                                        {
                                            ...ad,
                                            cr,
                                            roi,
                                            profit
                                        },
                                        globalAverages
                                    );
                                    
                                    return (
                                        <TableRow 
                                            key={ad.adId} 
                                            sx={{ 
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                // Подсветка строк с проблемами только если достаточно данных
                                                bgcolor: performance.hasEnoughData 
                                                    ? (performance.status === 'critical' 
                                                        ? 'rgba(255, 0, 0, 0.05)' 
                                                        : performance.status === 'warning' 
                                                            ? 'rgba(255, 152, 0, 0.05)' 
                                                            : 'inherit')
                                                    : 'inherit'
                                            }}
                                        >
                                            <TableCell component="th" scope="row">
                                                {ad.adId}
                                            </TableCell>
                                            <TableCell>
                                                {renderPerformanceStatus(performance)}
                                            </TableCell>
                                            <TableCell>{ad.clicks}</TableCell>
                                            <TableCell>{ad.leads}</TableCell>
                                            <TableCell sx={{ color: getCRColor(cr) }}>
                                                {cr.toFixed(2)}
                                            </TableCell>
                                            <TableCell>{ad.cost.toFixed(2)}</TableCell>
                                            <TableCell>{ad.revenue.toFixed(2)}</TableCell>
                                            <TableCell sx={{ color: getProfitColor(profit) }}>
                                                {profit.toFixed(2)}
                                            </TableCell>
                                            <TableCell sx={{ 
                                                color: getRoiColor(roi),
                                                fontWeight: Math.abs(roi) > 50 ? 'bold' : 'normal'
                                            }}>
                                                {roi.toFixed(2)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        Нет данных за выбранный период
                                    </TableCell>
                                </TableRow>
                            )}
                            {adsData.length > 0 && (
                                <TableRow
                                    sx={{ 
                                        '& td': { 
                                            fontWeight: 'bold', 
                                            borderTop: '2px solid rgba(224, 224, 224, 1)'
                                        } 
                                    }}
                                >
                                    <TableCell component="th" scope="row">ИТОГО</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>
                                        {adsData.reduce((sum, ad) => sum + ad.clicks, 0)}
                                    </TableCell>
                                    <TableCell>
                                        {adsData.reduce((sum, ad) => sum + ad.leads, 0)}
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const totalClicks = adsData.reduce((sum, ad) => sum + ad.clicks, 0);
                                            const totalLeads = adsData.reduce((sum, ad) => sum + ad.leads, 0);
                                            const totalCR = totalClicks > 0 ? (totalLeads / totalClicks * 100) : 0;
                                            return (
                                                <span style={{ color: getCRColor(totalCR) }}>
                                                    {totalCR.toFixed(2)}
                                                </span>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        {adsData.reduce((sum, ad) => sum + ad.cost, 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell>
                                        {adsData.reduce((sum, ad) => sum + ad.revenue, 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const totalRevenue = adsData.reduce((sum, ad) => sum + ad.revenue, 0);
                                            const totalCost = adsData.reduce((sum, ad) => sum + ad.cost, 0);
                                            const totalProfit = totalRevenue - totalCost;
                                            return (
                                                <span style={{ color: getProfitColor(totalProfit) }}>
                                                    {totalProfit.toFixed(2)}
                                                </span>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const totalRevenue = adsData.reduce((sum, ad) => sum + ad.revenue, 0);
                                            const totalCost = adsData.reduce((sum, ad) => sum + ad.cost, 0);
                                            const totalRoi = totalCost > 0 ? (((totalRevenue - totalCost) / totalCost) * 100) : 0;
                                            return (
                                                <span style={{ 
                                                    color: getRoiColor(totalRoi),
                                                    fontWeight: Math.abs(totalRoi) > 50 ? 'bold' : 'normal'
                                                }}>
                                                    {totalRoi.toFixed(2)}
                                                </span>
                                            );
                                        })()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default AdsStats;